/**
 * Base Strategy Abstract Class
 *
 * Provides common functionality for all best-fit strategies while enforcing
 * the strategy interface contract. Concrete strategies extend this class and
 * implement the abstract methods specific to their optimization approach.
 */

import { createCandidateFromMillage } from '~/builders/candidate-builder';
import { generateSpecificShape } from '~/engine/millage/shape-generators/general-processing';
import { getShapeAfter, getShapeBefore } from '~/engine/millage/shape-progression';
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import type {
  BestFitStrategy,
  ProjectType,
  SearchContext,
  SearchDetails,
  SearchDirection,
  SearchResult,
  SearchStoppingReason,
} from './strategy-types';

/**
 * Abstract base class for all best-fit strategies.
 * Provides common utilities while requiring strategies to implement their specific logic.
 */
export abstract class BaseBestFitStrategy implements BestFitStrategy {
  abstract readonly strategyType: ProjectType;
  abstract readonly strategyName: string;
  abstract readonly description: string;

  // Abstract methods that each strategy must implement
  abstract isViable(candidate: Candidate, context?: SearchContext): boolean;
  abstract selectBestFirstFit(
    candidates: Candidate[],
    configuration: Configuration
  ): Candidate | null;
  abstract executeSearch(
    startingCandidate: Candidate,
    context: SearchContext
  ): Promise<SearchResult>;
  abstract getPrimaryMetric(candidate: Candidate): number;
  abstract getPrimaryMetricName(): string;

  /**
   * Common validation logic for all strategies.
   * Individual strategies can override for additional validation.
   */
  validateCandidates(candidates: Candidate[]): string | null {
    if (!candidates || candidates.length === 0) {
      return 'No candidates provided';
    }

    const firstFitCandidates = candidates.filter((c) => c.createdInPhase === 'first-fit');
    if (firstFitCandidates.length === 0) {
      return 'No first-fit candidates found';
    }

    const viableCandidates = firstFitCandidates.filter((c) => this.isViable(c));
    if (viableCandidates.length === 0) {
      return `No viable candidates found for ${this.strategyName} strategy`;
    }

    return null;
  }

  /**
   * Common candidate creation logic shared across strategies.
   * Creates and processes a candidate from a shape ID.
   */
  protected createCandidateFromShape(context: SearchContext, shapeId: string): Candidate | null {
    try {
      const { projectData, configuration, project } = context;
      // Generate millage information for the specific shape
      const millageInfo = generateSpecificShape(projectData, configuration, shapeId);

      // Create candidate using the existing factory function
      const candidate = createCandidateFromMillage(
        project,
        configuration,
        millageInfo,
        'BestFit',
        'best-fit'
      );

      logger.L3.info(`🔧 Created candidate ${candidate.name} from shape ${shapeId}`);

      return candidate;
    } catch (error) {
      logger.error(
        `❌ Failed to create candidate from shape ${shapeId}:`,
        error instanceof Error ? error : undefined
      );
      return null;
    }
  }

  /**
   * Common shape progression logic.
   * Gets the next shape in the specified direction.
   */
  protected getNextShape(currentShapeId: string, direction: SearchDirection): string | null {
    return direction === 'forward' ? getShapeAfter(currentShapeId) : getShapeBefore(currentShapeId);
  }

  /**
   * Common logging utility for search progress.
   */
  protected logSearchProgress(
    candidateName: string,
    metricValue: number,
    metricName: string,
    improvement: number | null = null
  ): void {
    const improvementText =
      improvement !== null ? ` (${improvement > 0 ? '+' : ''}${improvement.toFixed(3)})` : '';

    logger.L3.info(
      `🔍 ${candidateName}: ${metricName} = ${metricValue.toFixed(3)}${improvementText}`
    );
  }

  /**
   * Common utility to create search details object.
   */
  protected createSearchDetails(
    startingCandidate: Candidate,
    candidatesGenerated: number,
    chosenDirection: SearchDirection | null = null,
    finalMetricValue: number,
    improvementAchieved: number
  ): SearchDetails {
    return {
      startingCandidate,
      candidatesGenerated,
      chosenDirection,
      finalMetricValue,
      improvementAchieved,
      directionsExplored: chosenDirection ? [chosenDirection] : [],
    };
  }

  /**
   * Common utility to create search result object.
   */
  protected createSearchResult(
    bestCandidate: Candidate,
    allCandidates: Candidate[],
    searchPath: string[],
    stoppingReason: SearchStoppingReason,
    searchDetails: SearchDetails,
    processingTime: number
  ): SearchResult {
    return {
      bestCandidate,
      allCandidates,
      searchPath,
      stoppingReason,
      searchDetails,
      processingTime,
    };
  }

  /**
   * Common utility for filtering first-fit candidates.
   */
  protected getFirstFitCandidates(candidates: Candidate[]): Candidate[] {
    return candidates.filter((c) => c.createdInPhase === 'first-fit');
  }

  /**
   * Common utility for filtering viable candidates according to this strategy.
   */
  protected getViableCandidates(candidates: Candidate[]): Candidate[] {
    return candidates.filter((c) => this.isViable(c));
  }

  /**
   * Common logging for strategy start.
   */
  protected logStrategyStart(candidatesCount: number, startingCandidate: Candidate | null): void {
    logger.L3.debug(`🎯 ${this.strategyName} Strategy Started`);
    logger.L3.debug(`   Candidates: ${candidatesCount}`);
    logger.L3.debug(`   Starting from: ${startingCandidate?.name || 'None'}`);
    logger.L3.debug(`   Optimizing: ${this.description}`);
  }

  /**
   * Common logging for strategy completion.
   */
  protected logStrategyComplete(result: SearchResult): void {
    const { bestCandidate, allCandidates, stoppingReason, processingTime } = result;
    const metricValue = this.getPrimaryMetric(bestCandidate);

    logger.L3.info(`✅ ${this.strategyName} Strategy Complete: ${bestCandidate.name}`);
    logger.L3.debug(`   Strategy details`, {
      primaryMetric: `${this.getPrimaryMetricName()}: ${metricValue.toFixed(3)}`,
      candidatesGenerated: allCandidates.length,
      stoppingReason,
      processingTimeMs: processingTime,
    });
  }
}
